import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Settings, X } from "lucide-react";
import { cn } from "@/cn";
import { CoMark } from "@/components/co-mark";
import { SideNav } from "@/components/side-nav";
import { ThemeToggle } from "@/components/theme-toggle";
import { instrumentSerif } from "@/lib/fonts";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useApi";
import { useMobileNav } from "@/components/mobile-nav-context";
import { UserAvatar } from "@/components/user-avatar";

const STYLE = `
.co-mnav{position:sticky;top:0;z-index:30;background:var(--bg);padding-top:calc(env(safe-area-inset-top) + .8rem)}
.co-mscrim{position:fixed;inset:0;z-index:60;background:rgba(8,8,12,.45);-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);opacity:0;pointer-events:none;transition:opacity .3s ease}
.co-mscrim.open{opacity:1;pointer-events:auto}
.co-mdrawer{position:fixed;left:0;right:0;bottom:0;z-index:61;max-height:min(82vh,44rem);display:flex;flex-direction:column;overflow:hidden;overscroll-behavior:contain;transform:translateY(102%);transition:transform .38s cubic-bezier(.32,.72,0,1);will-change:transform;box-shadow:0 -18px 56px -20px rgba(0,0,0,.5);padding-bottom:calc(3.5rem + env(safe-area-inset-bottom));border-radius:1.5rem 1.5rem 0 0}
.co-mdrawer.open{transform:translateY(0)}
.co-msafe{padding-bottom:calc(1rem + env(safe-area-inset-bottom))}
@media(prefers-reduced-motion:reduce){.co-mdrawer,.co-mscrim{transition:none}}
`;

export function MobileNav() {
  const location = useLocation();
  const pathname = location.pathname;
  const { user, signOut } = useAuth();
  const { data: profile } = useProfile();
  const { drawerOpen: open, setDrawerOpen: setOpen, closeDrawer } = useMobileNav();
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    closeDrawer();
  }, [pathname, closeDrawer]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const f = panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (f.length === 0) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    const t = window.setTimeout(
      () => panelRef.current?.querySelector<HTMLElement>("a, button")?.focus(),
      60,
    );
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
    };
  }, [open, setOpen]);

  const drag = useRef({ y0: 0, dy: 0, active: false });
  const onTouchStart = (e: React.TouchEvent) => {
    drag.current = { y0: e.touches[0].clientY, dy: 0, active: true };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!drag.current.active || !panelRef.current) return;
    const dy = Math.max(0, e.touches[0].clientY - drag.current.y0);
    drag.current.dy = dy;
    panelRef.current.style.transition = "none";
    panelRef.current.style.transform = `translateY(${dy}px)`;
  };
  const onTouchEnd = () => {
    if (!panelRef.current) return;
    panelRef.current.style.transition = "";
    panelRef.current.style.transform = "";
    if (drag.current.dy > 90) setOpen(false);
    drag.current.active = false;
  };

  return (
    <>
      <style>{STYLE}</style>

      <header className="co-mnav border-b border-border px-4 pb-3 md:hidden">
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="flex items-center gap-2"
            aria-label="ceo-cmd home"
          >
            <CoMark size={26} />
            <span
              className={`${instrumentSerif.className} relative -top-px text-xl text-landing`}
            >
              ceo-cmd
            </span>
          </Link>
          <Link
            to="/settings/account"
            className="ml-auto inline-flex min-h-[44px] min-w-[40px] items-center justify-center rounded-md p-2 text-muted hover:bg-surface-hover hover:text-foreground"
            aria-label="Settings"
          >
            <Settings className="size-4" />
          </Link>
        </div>
      </header>

      <div
        className={cn("co-mscrim md:hidden", open && "open")}
        onClick={() => setOpen(false)}
        aria-hidden
      />

      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        inert={!open}
        className={cn(
          "co-mdrawer border-t border-border bg-surface md:hidden",
          open && "open",
        )}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="flex shrink-0 flex-col items-center pt-2">
          <div className="mb-1 h-1.5 w-12 rounded-full bg-border" aria-hidden />
        </div>
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 pb-3 pt-1">
          <div>
            <span
              className={`${instrumentSerif.className} block text-2xl text-landing`}
            >
              More
            </span>
            <span className="text-sm font-medium text-muted-foreground">
              Navigation and account
            </span>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="inline-flex min-h-[48px] min-w-[48px] items-center justify-center rounded-xl p-2 text-muted transition-colors hover:bg-surface-hover hover:text-foreground"
          >
            <X className="size-6" />
          </button>
        </div>

        <div className="min-h-0 overflow-y-auto overscroll-contain px-4 py-3">
          <SideNav mobile onNavigate={() => setOpen(false)} />
        </div>

        <div className="co-msafe mt-auto shrink-0 space-y-4 border-t border-border px-5 pt-4">
          <div className="flex min-w-0 items-center gap-3">
            <UserAvatar
              name={profile?.name ?? user?.name}
              email={profile?.email ?? user?.email}
              src={profile?.avatarUrl}
              className="size-12"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">
                {profile?.name ?? user?.name ?? "Account"}
              </p>
              <p className="truncate text-sm text-muted-foreground">
                {profile?.email ?? user?.email}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-base font-semibold">
            <Link
              to="/settings/account"
              onClick={() => setOpen(false)}
              className="flex min-h-[52px] items-center rounded-xl border border-border px-4 hover:bg-surface-hover"
            >
              Profile
            </Link>
            <Link
              to="/settings/ai"
              onClick={() => setOpen(false)}
              className="flex min-h-[52px] items-center rounded-xl border border-border px-4 hover:bg-surface-hover"
            >
              AI providers
            </Link>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void signOut();
              }}
              className="col-span-2 flex min-h-[52px] w-full items-center rounded-xl px-4 text-left text-muted-foreground hover:bg-surface-hover hover:text-foreground"
            >
              Sign out
            </button>
          </div>
          <div className="flex items-center justify-between">
            <span
              className={`${instrumentSerif.className} text-sm text-faint`}
            >
              ceo-cmd · v0
            </span>
            <ThemeToggle />
          </div>
        </div>
      </aside>
    </>
  );
}
